import LinearProgress from "@mui/material/LinearProgress";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import { formatCurrency } from "@poseidon/utils/currency";
import React, { useEffect } from "react";
import { styled } from "@mui/material/styles";
import { useCalculateSettlement } from "../hooks/useCalculateSettlement";
import { useProcessSettlement } from "../hooks/useProcessSettlement";
import { Panel } from "@poseidon/ui/components/Panel";
import { getDate } from "@poseidon/firebase/timestamp";
import { useSettlementFigures } from "../hooks/useSettlementFigures";
import { usePermissions } from "@poseidon/ui/components";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import { useTimeMachine } from "@poseidon/firebase/hooks/useTimeMachine";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const EXPIRED_MESSAGE =
  "This settlement figure has expired and can't be processed - calculate a new settlement figure";

// Same rule as the backend: a figure expires once its valid-until date is reached
const isExpired = (validUntil, now) => !validUntil || validUntil <= now;

const SettlementFigureRow = ({
  data,
  id,
  now,
  processSaving,
  handleProcessSettlement,
  canWrite,
}) => {
  const expired =
    data.status === "valid" && isExpired(getDate(data.validUntil), now);
  return (
    <StyledTableRow>
      <TableCell>{getDate(data.date)?.toLocaleDateString("en-GB")}</TableCell>
      <TableCell>
        {expired ? (
          <Tooltip title={EXPIRED_MESSAGE}>
            <Chip label="expired" color="error" size="small" />
          </Tooltip>
        ) : (
          data.status
        )}
      </TableCell>
      <TableCell>
        {getDate(data.validUntil)?.toLocaleDateString("en-GB")}
      </TableCell>
      <TableCell>{formatCurrency(data.nextPaymentInterest)}</TableCell>
      <TableCell>{formatCurrency(data.additionalInterest)}</TableCell>
      <TableCell>{formatCurrency(data.principal)}</TableCell>
      <TableCell sx={{ fontWeight: "medium" }}>
        {formatCurrency(data.total)}
      </TableCell>
      <TableCell>
        {expired && (
          <Tooltip title={EXPIRED_MESSAGE}>
            <span>
              <Button variant="outlined" size="small" disabled>
                Process
              </Button>
            </span>
          </Tooltip>
        )}
        {data.status === "valid" && !expired && (
          <Button
            variant="outlined"
            size="small"
            disabled={processSaving || !canWrite}
            onClick={() => handleProcessSettlement(id)}
          >
            Process
          </Button>
        )}
      </TableCell>
    </StyledTableRow>
  );
};

export const SettlementsTable = ({ agreement }) => {
  const { canWrite } = usePermissions();
  const { settlementFigures, loading, error } = useSettlementFigures(
    agreement.id
  );
  const { calculateSettlement, saving } = useCalculateSettlement(agreement.id);
  const {
    processSettlement,
    saving: processSaving,
    error: processError,
  } = useProcessSettlement();
  const { setError } = useErrorMessage();
  const { timeMachine } = useTimeMachine();
  // Time machine is a dev-only testing aid; when its singleton doc is absent
  // (e.g. production) fall back to the real current date.
  const now = timeMachine?.now ?? new Date();
  useEffect(() => {
    if (processError) {
      setError(processError.message);
    }
  }, [processError, setError]);
  const handleProcessSettlement = (settlementId) => {
    processSettlement(agreement.id, settlementId);
  };
  return (
    <Panel nopadding expandable title="Settlement Figures">
      {loading && <LinearProgress />}
      {error && "Error!"}
      {!loading && !error && settlementFigures && (
        <Table>
          <TableHead>
            <StyledTableRow>
              <TableCell component="th">Date</TableCell>
              <TableCell component="th">Status</TableCell>
              <TableCell component="th">Valid Until</TableCell>
              <TableCell component="th">Interest (Next payment)</TableCell>
              <TableCell component="th">Interest (28 days)</TableCell>
              <TableCell component="th">Capital</TableCell>
              <TableCell component="th">Total</TableCell>
              <TableCell component="th"></TableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {settlementFigures.map((sf) => (
              <SettlementFigureRow
                key={sf.id}
                id={sf.id}
                data={sf}
                now={now}
                handleProcessSettlement={handleProcessSettlement}
                processSaving={processSaving}
                canWrite={canWrite}
              />
            ))}
          </TableBody>
        </Table>
      )}
      {saving && <LinearProgress />}
      {agreement.status !== "closed" && canWrite && (
        <Button
          sx={{ m: 2 }}
          onClick={() => calculateSettlement()}
          disabled={saving}
          variant="outlined"
        >
          {" "}
          Calculate Settlement
        </Button>
      )}
    </Panel>
  );
};
